import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Ícone à esquerda do título — ancora o modal ao módulo que o abriu. */
  icon?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  headerActions?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';
}

const SIZE_CLASS: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  '2xl': 'max-w-4xl',
  '3xl': 'max-w-6xl',
  full: 'max-w-[90vw] w-[90vw]',
};

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  icon,
  children,
  footer,
  headerActions,
  size = 'md',
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* Sem backdrop-filter (blur/saturate): em conteúdo grande do modal,
            cada scroll interno disparava re-render do filtro = flicker.
            Compensamos com overlay mais opaco (bg-black/55). */}
        <Dialog.Overlay
          className="fixed inset-0 z-[2000] bg-black/55 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-[2000] -translate-x-1/2 -translate-y-1/2',
            'w-[calc(100%-2rem)] max-h-[calc(100vh-2rem)] rounded-lg border border-border bg-elevated shadow-[var(--shadow-over)]',
            'focus:outline-none focus-visible:outline-none',
            'flex flex-col',
            SIZE_CLASS[size],
          )}
        >
          <div
            className={cn(
              'flex justify-between gap-4 px-6 pt-6 pb-3 shrink-0',
              // sem descrição o título é uma linha só: centra com o ícone
              description ? 'items-start' : 'items-center',
            )}
          >
            <div className="flex min-w-0 items-center gap-3.5">
              {icon && (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-accent text-[var(--on-solid)]">
                  {icon}
                </span>
              )}
              <div className="flex min-w-0 flex-col gap-1">
                <Dialog.Title className="text-[1.05rem] font-semibold tracking-tight text-text">
                  {title}
                </Dialog.Title>
                {description && (
                  <Dialog.Description className="text-sm text-text-2">{description}</Dialog.Description>
                )}
              </div>
            </div>
            {headerActions && (
              <div className="flex items-center gap-2 shrink-0">{headerActions}</div>
            )}
          </div>
          <div className="flex flex-col gap-3 px-6 pb-6 overflow-y-auto flex-1 min-h-0">
            {children}
          </div>
          {footer && (
            <div className="flex justify-end gap-2 px-6 py-3 border-t border-border shrink-0">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
